import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import {
  Paper,
  Grid,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  CircularProgress,
  Box,
} from "@material-ui/core";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import TrendingDownIcon from "@material-ui/icons/TrendingDown";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import VisibilityIcon from "@material-ui/icons/Visibility";
import utils from "../../universal/utils";

const styles = (theme) => ({
  root: {
    width: "100%",
  },
  productCard: {
    marginBottom: theme.spacing(3),
  },
  productHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  productImage: {
    width: 200,
    height: 200,
    objectFit: "cover",
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
  },
  productImagePlaceholder: {
    width: 200,
    height: 200,
    fontSize: "4rem",
  },
  productInfo: {
    flex: 1,
  },
  productName: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  detailRow: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  detailLabel: {
    fontWeight: 500,
    minWidth: 120,
    color: theme.palette.text.secondary,
  },
  detailValue: {
    color: theme.palette.text.primary,
  },
  metricsGrid: {
    marginTop: theme.spacing(2),
  },
  metricCard: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  metricHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  metricIcon: {
    color: theme.palette.primary.main,
  },
  metricValue: {
    fontSize: "2rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  metricLabel: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  metricChange: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    fontSize: "0.875rem",
  },
  positive: {
    color: theme.palette.success.main,
  },
  negative: {
    color: theme.palette.error.main,
  },
  scoreChip: {
    marginRight: theme.spacing(1),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
});

const hebrewLabels = {
  loading: "טוען נתונים...",
  productDetails: "פרטי מוצר",
  keyMetrics: "מדדי ביצועים עיקריים",
  details: {
    brand: "מותג",
    model: "דגם",
    displayName: "שם תצוגה",
    displaySize: "גודל",
    ean: "EAN",
    price: "מחיר",
    stock: "מלאי",
    score: "ניקוד",
  },
  metrics: {
    unitsSold: "יחידות שנמכרו",
    totalRevenue: "הכנסות כוללות",
    conversionRate: "שיעור המרה",
    addToCartRate: "שיעור הוספה לעגלה",
    impressions: "צפיות",
    profitMargin: "רווחיות",
    last30Days: "(30 ימים אחרונים)",
    last90Days: "(90 ימים אחרונים)",
    directSales: "מכירות ישירות",
    setSales: "מכירות דרך סטים",
    includedInSets: "נכלל ב-{count} סטים",
  },
  stockStatus: {
    inStock: "במלאי",
    lowStock: "מלאי נמוך",
    outOfStock: "אזל מהמלאי",
  },
  scoreRating: {
    excellent: "מצוין",
    good: "טוב",
    average: "ממוצע",
    poor: "חלש",
  },
};

const basePath = utils.getEdgeBasePath();

class ProductAnalyticsDisplay extends React.Component {
  _isMounted = false;

  state = {
    loading: true,
    error: null,
    analyticsData: null,
  };

  componentDidMount() {
    this._isMounted = true;
    this.loadAnalytics();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.product?.ean !== this.props.product?.ean) {
      this.loadAnalytics();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  loadAnalytics = async () => {
    const { product } = this.props;

    if (!product || !product.ean) {
      return;
    }

    this.setState({ loading: true, error: null });

    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/product-analytics/${product.ean}/complete`,
        { headers: utils.getAdminAuthHeaders() }
      );

      const data = await response.json();

      if (this._isMounted) {
        if (data.ok) {
          this.setState({
            analyticsData: data,
            loading: false,
          });
        } else {
          this.setState({
            error: data.error || "Failed to load analytics",
            loading: false,
          });
        }
      }
    } catch (error) {
      console.error("Error loading analytics:", error);
      if (this._isMounted) {
        this.setState({
          error: error.message,
          loading: false,
        });
      }
    }
  };

  getProductImageUrl = (product) => {
    if (!product || !product.cdn || !Array.isArray(product.cdn) || product.cdn.length === 0) {
      return null;
    }

    const primaryImage = product.cdn.find(
      (img) => img.primay === true || img.primary === true
    );
    const imageToUse = primaryImage || product.cdn[0];

    if (imageToUse && imageToUse.bucketPath) {
      return `https://s3.eu-west-2.amazonaws.com/sportyshop/${imageToUse.bucketPath}`;
    }

    return null;
  };

  getStockStatus = (stock) => {
    if (!stock || stock === 0) {
      return { label: hebrewLabels.stockStatus.outOfStock, color: "secondary" };
    } else if (stock < 5) {
      return { label: hebrewLabels.stockStatus.lowStock, color: "default" };
    }
    return { label: hebrewLabels.stockStatus.inStock, color: "primary" };
  };

  getScoreRating = (score) => {
    if (score >= 70) {
      return { label: hebrewLabels.scoreRating.excellent, color: "primary" };
    } else if (score >= 50) {
      return { label: hebrewLabels.scoreRating.good, color: "primary" };
    } else if (score >= 30) {
      return { label: hebrewLabels.scoreRating.average, color: "default" };
    }
    return { label: hebrewLabels.scoreRating.poor, color: "secondary" };
  };

  formatNumber = (num) => {
    if (num === null || num === undefined) return "-";
    return new Intl.NumberFormat("he-IL").format(Math.round(num));
  };

  formatPercent = (num) => {
    if (num === null || num === undefined) return "-";
    return `${(num * 100).toFixed(2)}%`;
  };

  formatCurrency = (num) => {
    if (num === null || num === undefined) return "-";
    return `₪${this.formatNumber(num)}`;
  };

  render() {
    const { classes, product } = this.props;
    const { loading, error, analyticsData } = this.state;

    if (!product) {
      return null;
    }

    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress />
          <Typography variant="body2" style={{ marginRight: 16 }}>
            {hebrewLabels.loading}
          </Typography>
        </div>
      );
    }

    if (error) {
      return (
        <Paper className={classes.productCard} style={{ padding: 16 }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      );
    }

    const imageUrl = this.getProductImageUrl(product);
    const stockStatus = this.getStockStatus(product.stock);
    const score = analyticsData?.score?.score || 0;
    const scoreRating = this.getScoreRating(score);

    return (
      <div className={classes.root}>
        {/* Product Details Card */}
        <Paper className={classes.productCard}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {hebrewLabels.productDetails}
            </Typography>

            <div className={classes.productHeader}>
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={product.displayName || product.name}
                  className={classes.productImage}
                />
              ) : (
                <Avatar className={classes.productImagePlaceholder}>
                  {(product.brand || product.displayName || "P")[0].toUpperCase()}
                </Avatar>
              )}

              <div className={classes.productInfo}>
                <Typography variant="h5" className={classes.productName}>
                  {product.displayName || product.name}
                </Typography>

                <div className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>
                    {hebrewLabels.details.brand}:
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {product.brand || "-"}
                  </Typography>
                </div>

                {product.model && (
                  <div className={classes.detailRow}>
                    <Typography className={classes.detailLabel}>
                      {hebrewLabels.details.model}:
                    </Typography>
                    <Typography className={classes.detailValue}>
                      {product.model}
                    </Typography>
                  </div>
                )}

                <div className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>
                    {hebrewLabels.details.displaySize}:
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {product.displaySize || product.size || "-"}
                  </Typography>
                </div>

                <div className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>
                    {hebrewLabels.details.ean}:
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {product.ean}
                  </Typography>
                </div>

                <div className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>
                    {hebrewLabels.details.price}:
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {this.formatCurrency(product.price)}
                  </Typography>
                </div>

                <div className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>
                    {hebrewLabels.details.stock}:
                  </Typography>
                  <Chip
                    label={`${product.stock || 0} - ${stockStatus.label}`}
                    color={stockStatus.color}
                    size="small"
                  />
                </div>

                <div className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>
                    {hebrewLabels.details.score}:
                  </Typography>
                  <Chip
                    label={`${score.toFixed(1)} - ${scoreRating.label}`}
                    color={scoreRating.color}
                    size="small"
                    className={classes.scoreChip}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Paper>

        {/* Key Metrics Grid */}
        {analyticsData && (
          <Paper className={classes.productCard}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {hebrewLabels.keyMetrics}
              </Typography>

              <Grid container spacing={3} className={classes.metricsGrid}>
                {/* Units Sold */}
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" className={classes.metricCard}>
                    <CardContent>
                      <div className={classes.metricHeader}>
                        <ShoppingCartIcon className={classes.metricIcon} />
                        <Typography variant="subtitle2">
                          {hebrewLabels.metrics.unitsSold}
                        </Typography>
                      </div>
                      <Typography className={classes.metricValue}>
                        {this.formatNumber(analyticsData.velocity?.metrics?.unitsSold || 0)}
                      </Typography>
                      <Typography className={classes.metricLabel}>
                        {hebrewLabels.metrics.last30Days}
                      </Typography>
                      {analyticsData.velocity?.breakdown && (
                        <Box mt={1}>
                          <Typography variant="caption" display="block" color="textSecondary">
                            {hebrewLabels.metrics.directSales}: {this.formatNumber(analyticsData.velocity.breakdown.directSales || 0)}
                          </Typography>
                          <Typography variant="caption" display="block" color="textSecondary">
                            {hebrewLabels.metrics.setSales}: {this.formatNumber(analyticsData.velocity.breakdown.setSales || 0)}
                          </Typography>
                          {analyticsData.velocity.breakdown.setsIncluded > 0 && (
                            <Typography variant="caption" display="block" color="primary" style={{ marginTop: 4 }}>
                              {hebrewLabels.metrics.includedInSets.replace('{count}', analyticsData.velocity.breakdown.setsIncluded)}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Impressions */}
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" className={classes.metricCard}>
                    <CardContent>
                      <div className={classes.metricHeader}>
                        <VisibilityIcon className={classes.metricIcon} />
                        <Typography variant="subtitle2">
                          {hebrewLabels.metrics.impressions}
                        </Typography>
                      </div>
                      <Typography className={classes.metricValue}>
                        {this.formatNumber(analyticsData.velocity?.metrics?.impressions || 0)}
                      </Typography>
                      <Typography className={classes.metricLabel}>
                        {hebrewLabels.metrics.last30Days}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Conversion Rate */}
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" className={classes.metricCard}>
                    <CardContent>
                      <div className={classes.metricHeader}>
                        <TrendingUpIcon className={classes.metricIcon} />
                        <Typography variant="subtitle2">
                          {hebrewLabels.metrics.conversionRate}
                        </Typography>
                      </div>
                      <Typography className={classes.metricValue}>
                        {this.formatPercent(analyticsData.velocity?.rates?.conversionRate || 0)}
                      </Typography>
                      <Typography className={classes.metricLabel}>
                        {hebrewLabels.metrics.last30Days}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Add to Cart Rate */}
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" className={classes.metricCard}>
                    <CardContent>
                      <div className={classes.metricHeader}>
                        <ShoppingCartIcon className={classes.metricIcon} />
                        <Typography variant="subtitle2">
                          {hebrewLabels.metrics.addToCartRate}
                        </Typography>
                      </div>
                      <Typography className={classes.metricValue}>
                        {this.formatPercent(analyticsData.velocity?.rates?.addToCartRate || 0)}
                      </Typography>
                      <Typography className={classes.metricLabel}>
                        {hebrewLabels.metrics.last30Days}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Profit Margin */}
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" className={classes.metricCard}>
                    <CardContent>
                      <div className={classes.metricHeader}>
                        <AttachMoneyIcon className={classes.metricIcon} />
                        <Typography variant="subtitle2">
                          {hebrewLabels.metrics.profitMargin}
                        </Typography>
                      </div>
                      <Typography className={classes.metricValue}>
                        {this.formatPercent(analyticsData.profitability?.product?.currentMargin || 0)}
                      </Typography>
                      <Typography className={classes.metricLabel}>
                        {this.formatCurrency(analyticsData.profitability?.metrics?.totalProfit || 0)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Total Revenue */}
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" className={classes.metricCard}>
                    <CardContent>
                      <div className={classes.metricHeader}>
                        <AttachMoneyIcon className={classes.metricIcon} />
                        <Typography variant="subtitle2">
                          {hebrewLabels.metrics.totalRevenue}
                        </Typography>
                      </div>
                      <Typography className={classes.metricValue}>
                        {this.formatCurrency(analyticsData.profitability?.metrics?.totalRevenue || 0)}
                      </Typography>
                      <Typography className={classes.metricLabel}>
                        {hebrewLabels.metrics.last90Days}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Paper>
        )}
      </div>
    );
  }
}

ProductAnalyticsDisplay.propTypes = {
  classes: PropTypes.object.isRequired,
  product: PropTypes.object,
};

export default withStyles(styles)(ProductAnalyticsDisplay);
